import React from 'react'
import styled from 'styled-components'
import {
  useAddressForm,
  useConfigState,
  AddressFormModule,
  useGenericCourierAddress,
  UseCheckoutSpeedyAddressResults,
  UseCheckoutEvropatAddressResults,
  UseCheckoutEcontAddressResults,
  useGlobalLabels,
} from '@sentecacommerce-theme/lib'
import {
  EvropatAddressForm,
  GenericAddressForm,
  GenericOfficeForm,
} from './Components'
import { Spinner } from '../../Spinner'
import { IconEcont, IconSpeedy } from '../../Icons'
import { InputLabel as Label, Input } from '../../Input'
import { TabRadio } from '../../TabRadio'

import { FormError } from '../../FormError'

const iconsMap: any = {
  Speedy: <IconSpeedy style={{ width: '6.8rem', height: '3.4rem' }} />,
  Econt: (
    <IconEcont
      style={{
        width: '8.8rem',
        height: '5rem',
        marginTop: '-1.6rem',
        marginBottom: '-2.2rem',
        marginLeft: '-1.8rem',
      }}
    />
  ),
  Evropat: (
    <img
      src="https://production.hippoland-contents.com/platform/media/evroput-logo-13845.jpg"
      alt="Европът"
      style={{ width: '6.8rem', height: 'auto' }}
    />
  ),
}

export const AddressForm = () => {
  const {
    formKey,
    modules,
    isLoading,
    moduleData,
    addressState,
    onInputChange,
    errors,
  } = useAddressForm()

  const config = useConfigState()

  const econtMethods = useGenericCourierAddress<UseCheckoutEcontAddressResults>(
    {
      key: config.courierKeys.econt,
    }
  )

  const speedyMethods = useGenericCourierAddress<UseCheckoutSpeedyAddressResults>(
    {
      key: config.courierKeys.speedy,
    }
  )

  const evropatMethods = useGenericCourierAddress<UseCheckoutEvropatAddressResults>(
    {
      key: config.courierKeys.evropat,
    }
  )

  const { globalValidations, couriers, personalInformation } = useGlobalLabels()

  const formsMap = {
    'Econt-Address': <GenericAddressForm methods={econtMethods} />,
    'Speedy-Address': <GenericAddressForm methods={speedyMethods} />,
    'Evropat-Address': <EvropatAddressForm methods={evropatMethods} />,

    'Econt-Office': <GenericOfficeForm methods={econtMethods} />,
    'Speedy-Office': <GenericOfficeForm methods={speedyMethods} />,
    'Evropat-Office': <GenericOfficeForm methods={evropatMethods} />,
  }

  const moduleKeys = Object.keys(modules || {})

  const mod: AddressFormModule | undefined =
    modules?.[moduleData?.moduleKey as any]

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onInputChange?.(e.target.name, e.target.value)
  }

  if (isLoading) {
    return (
      <LoaderContainer>
        <Spinner />
      </LoaderContainer>
    )
  }

  return (
    <>
      <div className="row">
        <div className="col-12">
          <Title>{couriers?.pickACourier}</Title>
          <RequiredFields>
            {globalValidations?.requiredFields.toLowerCase()}
          </RequiredFields>
          <Label htmlFor="">
            <TabRadio
              tabStyle="button"
              name="adbook-deliveryProvider"
              options={moduleKeys.map((moduleKey) => {
                return {
                  key: moduleKey,
                  icon: iconsMap[moduleKey],
                  onClick: modules[moduleKey]?.methods?.[0]?.select,
                  label: moduleKey,
                  isSelected: moduleKey === moduleData?.moduleKey,
                }
              })}
              tabClassName="col-12 col-md-4"
            />
            <FormError errors={errors} fieldName="courier" />
          </Label>
        </div>
        <div className="col-12">
          <Label htmlFor="">
            <TabRadio
              tabStyle="button"
              name="adbook-deliveryProvider"
              options={
                mod?.methods?.map((method) => {
                  return {
                    key: method.key,
                    onClick: method.select,
                    label: method.key as string,
                    isSelected:
                      method.key === moduleData?.moduleData?.methodKey,
                  }
                }) || []
              }
              tabClassName="col-12 col-md-6"
            />
          </Label>
        </div>
        <React.Fragment key={formKey}>{formsMap[formKey]}</React.Fragment>
        <div className="col-12">
          <div className="pt-4">
            <Title>{personalInformation?.label}</Title>
          </div>
          <InputsWrapper>
            <DesktopHalf>
              <Label htmlFor="adbook-firstName">
                {`${personalInformation?.firstName} *`}
                <Input
                  value={addressState?.firstName || ''}
                  onChange={onChange}
                  id="adbook-firstName"
                  name="firstName"
                />
                <FormError errors={errors} fieldName="firstName" />
              </Label>
            </DesktopHalf>
            <DesktopHalf>
              <Label htmlFor="adbook-lastName">
                {`${personalInformation?.lastName} *`}
                <Input
                  value={addressState?.lastName || ''}
                  onChange={onChange}
                  id="adbook-lastName"
                  name="lastName"
                />
                <FormError errors={errors} fieldName="lastName" />
              </Label>
            </DesktopHalf>
            <DesktopHalf>
              <Label htmlFor="adbook-phoneNumber">
                {`${personalInformation?.number} *`}
                <Input
                  value={addressState?.phoneNumber || ''}
                  onChange={onChange}
                  name="phoneNumber"
                  id="adbook-phoneNumber"
                />
                <FormError errors={errors} fieldName="phoneNumber" />
              </Label>
            </DesktopHalf>
            <DesktopHalf>
              <Label htmlFor="adbook-email">
                {`${personalInformation?.email} *`}
                <Input
                  value={addressState?.email || ''}
                  onChange={onChange}
                  name="email"
                  id="adbook-email"
                />
                <FormError errors={errors} fieldName="email" />
              </Label>
            </DesktopHalf>
          </InputsWrapper>
        </div>
      </div>
      <FormError errors={errors} fieldName="default" />
    </>
  )
}

const LoaderContainer = styled.div`
  position: relative;
  height: 24rem;
`

const InputsWrapper = styled.div.attrs({
  className: 'row',
})``

const DesktopHalf = styled.div.attrs({
  className: 'col-12 col-sm-6',
})``

const Title = styled.h6.attrs({
  className: 'mb-3',
})<{ paddingTop?: boolean }>`
  padding-top: ${(props) => (props.paddingTop ? '1.5rem' : 0)};
`

const RequiredFields = styled.p.attrs({
  className: 'mb-4',
})`
  color: ${({ theme }) => theme.palette.hint};
`
